import React, { useMemo, useState, useEffect } from 'react';
import { useTaps } from '../hooks/useTaps';
import { useBeersMap } from '../hooks/useBeers';
import { useKickTap } from '../hooks/useTapMutations';
import { useLocation } from '../lib/location';
import { useToast } from '../lib/toast';
import { Icon } from '../components/Icon';
import { SwapModalWrapper } from '../components/SwapModal';
import { ReservationsPage } from './ReservationsPage';
import type { Beer, KegSize, Tap } from '../api/types';

type KioskTab = 'taps' | 'reservations';

// ---------------------------------------------------------------------------
// Helpers (copied from TapsPage — kept local to avoid coupling)
// ---------------------------------------------------------------------------

const KEG_SIZE_LABEL: Record<KegSize, string> = {
  half_keg: 'Half-barrel',
  keg:      'Full keg',
  sixtel:   'Sixtel',
  other:    'Other',
};

function hoursAgo(iso: string): string {
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 36e5);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

type TapStatus = 'on' | 'kicked' | 'empty';

function tapStatus(tap: Tap): TapStatus {
  if (!tap.currentAssignment) return tap.mostRecentKicked ? 'kicked' : 'empty';
  return 'on';
}

// ---------------------------------------------------------------------------
// KioskTapCard — same visual design as TapCard but no Details button
// ---------------------------------------------------------------------------

interface KioskTapCardProps {
  tap: Tap;
  beer: Beer | undefined;
  kickedBeer: Beer | undefined;
  status: TapStatus;
  onKick: () => void;
  onSwap: () => void;
}

function KioskTapCard({ tap, beer, kickedBeer, status, onKick, onSwap }: KioskTapCardProps) {
  if (status === 'empty') {
    return (
      <div style={{
        background: 'var(--bg-elevated)', border: '1px dashed var(--border)',
        borderRadius: 8, padding: 20,
        display: 'flex', flexDirection: 'column', gap: 8, minHeight: 196,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="uppercase-label">Tap {tap.tapNumber}{tap.label ? ` · ${tap.label}` : ''}</div>
          <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 4,
            fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
            border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-subtle)',
          }}>EMPTY</span>
        </div>
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-subtle)', fontSize: '0.875rem',
        }}>
          No keg assigned
        </div>
        <button
          onClick={onSwap}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontWeight: 700, fontSize: '0.8125rem', padding: '6px 12px',
            borderRadius: 6, background: 'var(--bg-subtle)', color: 'var(--text)',
            border: '1px solid var(--border)', cursor: 'pointer',
          }}
        >
          <Icon name="plus" size={14} /> Tap a keg
        </button>
      </div>
    );
  }

  const displayBeer = status === 'on' ? beer : kickedBeer;
  const assignment = status === 'on' ? tap.currentAssignment : tap.mostRecentKicked;
  const isKicked = status === 'kicked';

  return (
    <div style={{
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
      borderRadius: 8, padding: 20,
      display: 'flex', flexDirection: 'column', gap: 14, minHeight: 196,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div className="uppercase-label">Tap {tap.tapNumber}{tap.label ? ` · ${tap.label}` : ''}</div>
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 4,
          fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
          border: '1px solid var(--border)',
          background: isKicked ? 'var(--bg-subtle)' : 'transparent',
          color: isKicked ? 'var(--text-muted)' : 'var(--text)',
        }}>{isKicked ? 'KICKED' : 'ON'}</span>
      </div>

      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2,
          color: isKicked ? 'var(--text-muted)' : 'var(--text)',
          textDecoration: isKicked ? 'line-through' : 'none',
        }}>
          {displayBeer?.name ?? '—'}
        </div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {displayBeer?.style && <span>{displayBeer.style}</span>}
          {displayBeer?.style && displayBeer?.abv != null && <span>·</span>}
          {displayBeer?.abv != null && <span className="mono">{displayBeer.abv}% ABV</span>}
        </div>
      </div>

      {assignment && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          <span>{assignment.kegSize ? (KEG_SIZE_LABEL[assignment.kegSize] ?? assignment.kegSize) : 'Unknown size'}</span>
          <span className="mono">Tapped {hoursAgo(assignment.tappedAt)}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        {isKicked ? (
          <button
            onClick={onSwap}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontWeight: 700, fontSize: '0.8125rem', padding: '6px 12px',
              borderRadius: 6, background: 'var(--text)', color: 'var(--bg)',
              border: '1px solid var(--text)', cursor: 'pointer',
            }}
          >
            <Icon name="swap" size={14} /> Swap keg
          </button>
        ) : (
          <button
            onClick={onKick}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontWeight: 700, fontSize: '0.8125rem', padding: '6px 12px',
              borderRadius: 6, background: 'transparent', color: 'var(--text)',
              border: '1px solid var(--border)', cursor: 'pointer',
            }}
          >
            Mark kicked
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KioskPage
// ---------------------------------------------------------------------------

export function KioskPage(): React.ReactElement {
  const { locationId, location } = useLocation();
  const { data, isLoading, isError } = useTaps(locationId);
  const { beerMap, isLoading: beersLoading } = useBeersMap();
  const kick = useKickTap(locationId);
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<KioskTab>('taps');
  const [swapTapId, setSwapTapId] = useState<string | null>(null);
  const [time, setTime] = useState(() => new Date());

  // Update clock every minute
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const taps = useMemo(
    () => (data?.items ?? []).sort((a, b) => a.tapNumber - b.tapNumber),
    [data],
  );

  const handleKick = (tap: Tap) => {
    if (!tap.currentAssignment) return;
    const beer = beerMap.get(tap.currentAssignment.beerId);
    kick.mutate(
      { assignmentId: tap.currentAssignment.id, promoteReserved: !!tap.reservedAssignment },
      {
        onSuccess: () => toast.success(`${beer?.name ?? 'Tap'} marked kicked.`),
        onError: () => toast.error('Failed to mark kicked. Try again.'),
      },
    );
  };

  const timeStr = time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <>
      {/* Minimal header — location name, tab switcher, clock */}
      <div style={{
        height: 52, display: 'flex', alignItems: 'center', gap: 16,
        padding: '0 24px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg)', flexShrink: 0,
      }}>
        <span style={{ fontWeight: 700, fontSize: '1rem', flexShrink: 0 }}>{location?.name ?? '—'}</span>

        <div style={{ display: 'flex', gap: 4, flex: 1, justifyContent: 'center' }}>
          {(['taps', 'reservations'] as KioskTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '5px 14px', borderRadius: 6, border: '1px solid',
                fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
                transition: 'background 0.15s, color 0.15s',
                background: activeTab === tab ? 'var(--text)' : 'transparent',
                color: activeTab === tab ? 'var(--bg)' : 'var(--text-muted)',
                borderColor: activeTab === tab ? 'var(--text)' : 'var(--border)',
              }}
            >
              {tab === 'taps' ? 'Tap list' : 'Reservations'}
            </button>
          ))}
        </div>

        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', flexShrink: 0 }} className="mono">{timeStr}</span>
      </div>

      {/* Reservations tab */}
      {activeTab === 'reservations' && (
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <ReservationsPage />
        </div>
      )}

      {/* Tap grid tab */}
      {activeTab === 'taps' && <div style={{ padding: '24px 24px 64px', overflowY: 'auto', flex: 1 }}>
        {isError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 20,
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderLeft: '3px solid var(--text)', borderRadius: 6,
          }}>
            <Icon name="alert" size={18} />
            <div style={{ fontSize: '0.875rem' }}>Could not load taps. Check your connection and try again.</div>
          </div>
        )}

        {(isLoading || beersLoading) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 8, minHeight: 196, animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            ))}
          </div>
        )}

        {!isLoading && !beersLoading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {taps.map(tap => {
              const st = tapStatus(tap);
              const beer = tap.currentAssignment ? beerMap.get(tap.currentAssignment.beerId) : undefined;
              const kickedBeer = tap.mostRecentKicked ? beerMap.get(tap.mostRecentKicked.beerId) : undefined;
              return (
                <KioskTapCard
                  key={tap.id}
                  tap={tap}
                  beer={beer}
                  kickedBeer={kickedBeer}
                  status={st}
                  onKick={() => handleKick(tap)}
                  onSwap={() => setSwapTapId(tap.id)}
                />
              );
            })}
          </div>
        )}

        {!isLoading && !beersLoading && taps.length === 0 && !isError && (
          <div style={{
            padding: 64, textAlign: 'center', color: 'var(--text-subtle)',
            border: '1px dashed var(--border)', borderRadius: 8,
          }}>
            No taps configured for this location.
          </div>
        )}
      </div>}

      {swapTapId && locationId && (
        <SwapModalWrapper
          tapId={swapTapId}
          taps={taps}
          locationId={locationId}
          onClose={() => setSwapTapId(null)}
          onAssigned={(name) => {
            const num = taps.find(t => t.id === swapTapId)?.tapNumber;
            toast.success(`Tap ${num} → ${name}.`);
            setSwapTapId(null);
          }}
        />
      )}
    </>
  );
}
